import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CourseService } from 'src/app/service/course.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/internal/Subject';
import { StudentService } from 'src/app/service/student.service';

interface Requirement {
  id: string;
  academicPurposeTypeId?: number | string;
  description: string;
  selected?: boolean;
  is_checked?: boolean;
  is_lrws?: boolean;
  scores?: {
    read?: number | null;
    write?: number | null;
    speak?: number | null;
    listen?: number | null;
  };
}

@Component({
  selector: 'app-admission-requirements',
  templateUrl: './admission-requirements.component.html',
  styleUrls: ['./admission-requirements.component.scss']
})
export class AdmissionRequirementsComponent implements OnInit, OnDestroy {
  @Input() courseId: number;
  @Input() instituteId: number;
  @Input() studentId: number;
  @Input() admissionReqData: any;
  @Input() isViewStudent: boolean = false;

  private destroyed$ = new Subject<void>();
  academicRequirements: Requirement[] = [];
  nonAcademicAdmissionRequirements: Requirement[] = [];

  academicTypeId = 4242;
  nonAcademicTypeId = 4243;
  formConfigs: any[] = [];
  selectedForm: any;
  FEES_FORMS = ["FEES_RECEIPT_INTERNATIONAL_STUDENT", "FEES_RECEIPT_OSAP", "FEES_RECEIPT_SELF_PR", "FEES_RECEIPT_INTERNATIONAL_STUDENT_REFUND", "FEES_RECEIPT_OSAP_REFUND", "FEES_RECEIPT_SELF_PR_REFUND"];
  configuredFormPayload: any[] = [];
  selectedFormId: number | null = null;
  addedForms: any[] = [];
  selectedFormIds: Array<number | null> = [null];

  constructor(
    private courseService: CourseService,
    private studentService: StudentService

  ) { }

  ngOnInit(): void {
    this.getAdmissionRequirements();
    this.getReports();
    // Prepopulate select dropdowns and fields for preselected forms
    if (this.admissionReqData && Array.isArray(this.admissionReqData.configurable_forms)) {
      // If admissionReqData is an object with configurable_forms array
      this.selectedFormIds = this.admissionReqData.configurable_forms.map((form: any) => form && form.formId);
    } else if (Array.isArray(this.admissionReqData)) {
      // If admissionReqData is an array of form objects
      this.selectedFormIds = this.admissionReqData
        .filter((form: any) => form && form.configurableFields && form.formId)
        .map((form: any) => form && form.formId);
    } else {
      this.selectedFormIds = [null];
    }
    // Academic/Non-Academic requirements
    if (Array.isArray(this.admissionReqData)) {
      this.academicRequirements = this.admissionReqData.find((d: any) => d.type === 'Academic')?.requirements || [];
      this.nonAcademicAdmissionRequirements = this.admissionReqData.find((d: any) => d.type === 'Non-Academic')?.requirements || [];
    } else {
      this.academicRequirements = this.admissionReqData["Admission Requirements"] || [];
      this.nonAcademicAdmissionRequirements = this.admissionReqData["Non-Academic Admission Requirements"] || [];
    }
  }

  getAdmissionRequirements(): void {
    this.courseService.getAdmissionRequirements(this.courseId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data: any) => {
        if (data && data.length > 0) {
          let requirements = data[0].requirements;
          if (this.academicRequirements.length === 0) {
            this.academicRequirements = requirements.filter((req: any) => req.academicPurposeTypeId == this.academicTypeId);
          } else {
            // Merge is_checked/is_lrws from API into existing requirements
            const apiAcademic = requirements.filter((req: any) => req.academicPurposeTypeId == this.academicTypeId);
            this.academicRequirements = this.mergeApiFlags(this.academicRequirements, apiAcademic);
          }
          if (this.nonAcademicAdmissionRequirements.length === 0) {
            this.nonAcademicAdmissionRequirements = requirements.filter(req => req.academicPurposeTypeId == this.nonAcademicTypeId);
          } else {
            // Merge is_checked/is_lrws from API into existing requirements
            const apiNonAcademic = requirements.filter((req: any) => req.academicPurposeTypeId == this.nonAcademicTypeId);
            this.nonAcademicAdmissionRequirements = this.mergeApiFlags(this.nonAcademicAdmissionRequirements, apiNonAcademic);
          }
          // Update selected status and scores based on existing selections
          this.academicRequirements = this.populateSelectedAdmissionRequirements(this.academicRequirements);
          this.nonAcademicAdmissionRequirements = this.populateSelectedAdmissionRequirements(this.nonAcademicAdmissionRequirements);
        }
      });
  }

  private mergeApiFlags(existing: Requirement[], apiData: any[]): Requirement[] {
    return existing.map(req => {
      const apiReq = apiData.find(a => a.description === req.description);
      if (apiReq) {
        req.is_checked = apiReq.is_checked || false;
        req.is_lrws = apiReq.is_lrws || false;
      }
      return req;
    });
  }

  private safeParseAdditionalInfo(additionalInfo: any): any {
    // Already an object
    if (additionalInfo && typeof additionalInfo === 'object') {
      return additionalInfo;
    }

    if (typeof additionalInfo !== 'string') {
      return {};
    }

    try {
      return JSON.parse(additionalInfo);
    } catch (e1) {
      // Attempt auto-fix: remove trailing commas and convert single quotes to double quotes
      try {
        const noTrailingCommas = additionalInfo.replace(/,\s*([}\]])/g, '$1');
        const normalizedQuotes = noTrailingCommas.replace(/'/g, '"');
        return JSON.parse(normalizedQuotes);
      } catch (e2) {
        console.warn('Failed to parse additionalInfo JSON for a form config:', { original: additionalInfo, error: e2 });
        return {};
      }
    }
  }

  getReports(): void {
    const companyId = JSON.parse(sessionStorage.getItem("companyId"));
    this.studentService.getStudentContarctCollegeForms(companyId, this.instituteId, this.courseId, this.studentId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        res => {
          if (res) {
            // Exclude FEES related forms in student contracts.
            this.formConfigs = res.filter(form => !this.FEES_FORMS?.includes(form?.formName));
            this.formConfigs = this.formConfigs.filter(form => form?.formType === "Configurable");
            this.formConfigs = this.formConfigs.map(form => {
              // Ensure additionalInfo is a parsed object with graceful error handling
              const parsed = this.safeParseAdditionalInfo(form.additionalInfo);
              return { ...form, additionalInfo: parsed };
            });

          } else {
            this.formConfigs = [];
          }
        },
        err => console.log(err)
      )
  }

  populateSelectedAdmissionRequirements(requirements: Requirement[]): Requirement[] {
    return requirements.map(req => {
      /*if (this.academicRequirements.find(r => r.academicPurposeTypeId == req.academicPurposeTypeId && r.description === req.description)) {
        selectedReq = this.academicRequirements.find(r => r.academicPurposeTypeId == req.academicPurposeTypeId && r.description === req.description);
      } else {
        selectedReq = this.nonAcademicAdmissionRequirements.find(r => r.academicPurposeTypeId == req.academicPurposeTypeId && r.description === req.description);
      }*/

      req.selected = req.selected || req.is_checked || false;
      req.is_checked = req.is_checked || false;
      req.is_lrws = req.is_lrws || false;
      req.scores = req.scores || { read: null, write: null, speak: null, listen: null };
      return req;
    });
  };

  populateFormConfigurations(): void {
    this.selectedFormIds = this.admissionReqData["configurable_forms"] || [];
  };

  onScoreChange(event: any, requirement: Requirement, scoreType: string): void {
    event?.stopPropagation();
    event?.preventDefault();
    // No need to reassign the array, ngModel already updates the value
    // If you need to do something with the updated value, do it here
  };

  onFormConfigChange(id: string): void {
    this.selectedForm = this.formConfigs.find(config => config.id == id);
  }

  addForm(): void {
    if (!this.selectedFormId) return;
    const formToAdd = this.formConfigs.find(config => config.id === this.selectedFormId);
    if (formToAdd && !this.addedForms.some(f => f.id === formToAdd.id)) {
      this.addedForms.push(formToAdd);
    }
  }

  removeForm(index: number): void {
    this.addedForms.splice(index, 1);
    // Optionally remove payload for deleted form
    this.configuredFormPayload.splice(index, 1);
  }

  addFormDropdown(): void {
    // Use immutable update to ensure change detection picks it up on first click
    this.selectedFormIds = [...this.selectedFormIds, null];
  }

  // TrackBy to distinguish multiple null entries so first + click adds a new row immediately
  trackByIndex(index: number, _item: any): number {
    return index;
  }

  removeFormDropdown(index: number): void {
    if (this.selectedFormIds.length > 1) {
      this.selectedFormIds.splice(index, 1);
      this.configuredFormPayload.splice(index, 1);
    }
  }

  addFormDropdownFromSelect(): void {
    if (this.selectedFormId && !this.selectedFormIds.includes(this.selectedFormId)) {
      this.selectedFormIds.push(this.selectedFormId);
      this.selectedFormId = null;
    }
  }

  onFormSelectionChanged(formId: number | null, index: number): void {
    if (!formId) {
      // Clear payload if selection is cleared
      this.configuredFormPayload = this.configuredFormPayload.filter((_, i) => i !== index);
      return;
    }
    const selectedConfig = this.getSelectedConfig(formId);
    if (!selectedConfig) return;
    const fields = selectedConfig?.additionalInfo?.configurableFields || [];
    // Initialize payload with current default/selected values from config
    this.configuredFormPayload[index] = {
      formId: selectedConfig.id,
      configurableFields: fields
    };
    console.log('Configured Forms (on select):', this.configuredFormPayload);
  }

  onFormValueChange(formValues: any, form: any): void {
    // Store payload for each form dropdown by index
    const idx = this.selectedFormIds.findIndex(id => id === form.id);
    if (idx === -1) {
      // Preview mode (no dropdown added yet); still emit to index 0 as a temporary payload
      this.configuredFormPayload[0] = {
        formId: form.id,
        configurableFields: formValues
      };
      console.log('Configured Forms (preview):', this.configuredFormPayload);
      return;
    }
    this.configuredFormPayload[idx] = {
      formId: form.id,
      configurableFields: formValues
    };
    console.log('Configured Forms:', this.configuredFormPayload);
  }

  getSelectedConfig(formId: number): any {
    return this.formConfigs.find(config => config.id == formId);
  }

  getAdmissionReqDataForForm(formId: number): any[] {
    // Always get the latest formId from selectedFormIds
    if (this.admissionReqData && Array.isArray(this.admissionReqData.configurable_forms)) {
      const formObj = this.admissionReqData.configurable_forms.find((form: any) => form && form.formId == formId);
      return formObj && Array.isArray(formObj.configurableFields) ? formObj.configurableFields : [];
    } else if (Array.isArray(this.admissionReqData)) {
      const formObj = this.admissionReqData.find((form: any) => form && form.formId == formId);
      return formObj && Array.isArray(formObj.configurableFields) ? formObj.configurableFields : [];
    }
    return [];
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

}
